import * as React from "react";
import { useCallback, useMemo, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import { MAX_TEXT_LENGTH } from "../../../../constants";
import {
  Validations,
  IAddEditStudySite,
  IAddEditStudySiteProtocol
} from "../../../../model";
import { MddValidatedTextField, MddCheckbox } from "../../../common";
import { updateProtocolInStudySite } from "./studySiteUtils";

interface IProtocolCardProps {
  protocol: IAddEditStudySiteProtocol;
  applyIdToAllProtocols?: boolean;
  studySite?: IAddEditStudySite;
  isEditMode?: boolean;
  isSiteActive?: boolean;
  onDelete?: (protocol: IAddEditStudySiteProtocol) => void;
  onStudySiteChange?: (studySite: IAddEditStudySite) => void;
  onValidationError?: (protocolId: string, hasError: boolean) => void;
}

export const MddStudySiteProtocolCard = ({
  protocol,
  applyIdToAllProtocols = false,
  studySite,
  isEditMode = false,
  isSiteActive = true,
  onDelete,
  onStudySiteChange,
  onValidationError
}: IProtocolCardProps): JSX.Element => {
  // Use the main sponsor site ID when applyIdToAllProtocols is true (add mode only)
  // Otherwise use the protocol's individual sponsor site ID
  const mainSponsorSiteId = studySite?.sponsorSiteId || "";

  const currentValue = useMemo(() => {
    // In edit mode, always use the protocol's individual sponsor site ID
    if (isEditMode) {
      return protocol.sponsorSiteId || "";
    }
    // In add mode, use main sponsor site ID if applyIdToAllProtocols is true
    return applyIdToAllProtocols
      ? mainSponsorSiteId
      : protocol.sponsorSiteId || "";
  }, [
    applyIdToAllProtocols,
    mainSponsorSiteId,
    protocol.sponsorSiteId,
    isEditMode
  ]);

  const isSponsorSiteIdDisabled =
    (applyIdToAllProtocols && !isEditMode) || protocol.isExisting;

  const handleSponsorSiteIdChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (onStudySiteChange && studySite) {
      const updatedStudySite = updateProtocolInStudySite(
        studySite,
        protocol.id,
        { sponsorSiteId: event.target.value }
      );
      onStudySiteChange(updatedStudySite);
    }
  };

  const handleProtocolActiveChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (onStudySiteChange && studySite) {
      const updatedStudySite = updateProtocolInStudySite(
        studySite,
        protocol.id,
        {
          isActive: event.target.checked
        }
      );
      onStudySiteChange(updatedStudySite);
    }
  };

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(protocol);
    }
  }, [protocol, onDelete]);

  const handleValidationError = useCallback(
    (validationError: boolean) => {
      if (onValidationError) {
        onValidationError(protocol.id, validationError);
      }
    },
    [protocol.id, onValidationError]
  );

  // Track previous value to detect programmatic changes
  const previousValueRef = useRef(currentValue);

  // Clear validation errors when value is programmatically updated with a valid value
  useEffect(() => {
    const previousValue = previousValueRef.current;
    const currentValueIsValid = currentValue && currentValue.trim().length > 0;

    // If the value changed programmatically (not through user input) and is now valid, clear validation errors
    if (
      previousValue !== currentValue &&
      currentValueIsValid &&
      onValidationError
    ) {
      onValidationError(protocol.id, false);
    }

    previousValueRef.current = currentValue;
  }, [currentValue, protocol.id, onValidationError]);

  const cardModifierClass = useMemo(() => {
    if (protocol.isExisting) {
      return "mdd-study-site--protocol-card--existing";
    }
    if (isEditMode) {
      return "mdd-study-site--protocol-card--new";
    }
    return "";
  }, [isEditMode, protocol.isExisting]);

  return (
    <div className={`mdd-study-site--protocol-card ${cardModifierClass}`}>
      {/* Header with title, active checkbox, and delete button */}
      <div className="mdd-study-site--protocol-card--header">
        <h3 className="mdd-study-site--protocol-card--title">
          {protocol.protocolName}
        </h3>
        <div className="mdd-study-site--protocol-card--header-controls">
          {protocol.isExisting && isEditMode && (
            <div className="mdd-study-site--protocol-card--active-checkbox">
              <MddCheckbox
                lblText="Active"
                chkBoxProps={{
                  id: `active_${protocol.id}`,
                  checked: protocol.isActive,
                  disabled: !isSiteActive,
                  onChange: handleProtocolActiveChange
                }}
              />
            </div>
          )}
          {!protocol.isExisting && (
            <FontAwesomeIcon
              icon={faCircleXmark}
              fixedWidth
              id="deleteCircleIcon"
              data-testid="deleteCircleIcon"
              className="svgEditIconPointer mdd-study-site--protocol-card--delete-icon"
              title="Delete"
              onClick={handleDelete}
            />
          )}
        </div>
      </div>

      {/* Sponsor Site ID Section */}
      <MddValidatedTextField
        id={`sponsorSiteId_${protocol.id}`}
        data-testid={`sponsorSiteId_${protocol.id}`}
        labelProps={{
          labelText: "Sponsor Site ID *",
          className: "mdd-study-site-div--sponsor-label-normal",
          showInfoIcon: true,
          infoIconTooltip:
            "Sponsor Site ID cannot be edited after adding the protocol participation",
          infoIconId: `sponsorSiteIdInfoIcon_${protocol.id}`,
          infoIconTestId: `sponsorSiteIdInfoIcon_${protocol.id}`
        }}
        textFieldProps={{
          textProps: {
            className: `mdd-personnel-div--editable-value mdd-study-site--protocol-card--sponsor-field${
              isSponsorSiteIdDisabled ? "-disabled" : "-enabled"
            } mdd-study-site--protocol-card--sponsor-field-fixed-width`,
            placeholder: "Enter Sponsor Site ID",
            inputProps: {
              id: `sponsorSiteIdTextField_${protocol.id}`,
              maxLength: MAX_TEXT_LENGTH.SPONSOR_SITE_ID
            },
            variant: "outlined",
            required: true,
            type: "text",
            onChange: handleSponsorSiteIdChange,
            value: currentValue,
            disabled: isSponsorSiteIdDisabled
          }
        }}
        validationForOnBlur={[
          Validations.required,
          Validations.sponsorSiteAllowedCharacters
        ]}
        validationerror={handleValidationError}
      />
    </div>
  );
};
